import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Button, ThemeProvider, createTheme, styled } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';

import ExcelDisplay from './ExcelDisplay';
import SessionExpiredModal from '../../../utils/SessionExpiredModal';
import PDFDisplay from './PDFDisplay';
import DocumentList from './DocumentList';
import FilterDocument from '../../Omni/Reports/FilterDocument';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

function groupByClientIdAndDocumentType(documents) {
  // Using reduce to group the documents
  const grouped = documents.reduce((acc, curr) => {
    // Check if this client_id already exists in accumulator
    const existingGroup = acc.find(group => group.clientId === curr.client_id);

    if (!existingGroup) {
      // If group doesn't exist, create new group
      acc.push({
        clientId: curr.client_id,
        clientName: curr.client_name,
        clientTFN: curr.rp_tfn, // Using rp_tfn (Reporting Party TFN) as clientTFN
      });
    }

    return acc;
  }, []);

  return grouped;
}

function groupByDocumentType(documents) {
  // Using reduce to group the documents
  const grouped = documents.reduce((acc, curr) => {
    // Check if this client_id already exists in accumulator
    const existingGroup = acc.find(group => group.document_type === curr.document_type);

    if (!existingGroup) {
      // If group doesn't exist, create new group
      acc.push({
        document_type: curr.document_type,
      });
    }

    return acc;
  }, []);

  return grouped;
}

const TextButton = styled(Button)(({ theme }) => ({
  color: 'white',
  textTransform: 'none',
  padding: '0.5vw 0.75vw',
}));

export default (props) => {
  const auth = useAuth();
  const [
    excelDisplayShow,
    setExcelDisplayShow,
  ] = useState(false);
  const [
    pdfDisplayShow,
    setPdfDisplayShow,
  ] = useState(false);
  const [
    displayMode,
    setDisplayMode,
  ] = useState('folder');
  const [
    selectedGroup,
    setSelectedGroup,
  ] = useState({ clientId: -1, clientName: '', clientAbn: '', documentTypes: [] });
  const [
    transformedData,
    setTransformedData,
  ] = useState([]);

  // Table
  const [
    data,
    setData,
  ] = useState([]);

  const [
    rawData,
    setRawData,
  ] = useState([]);

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  // Excel
  const [
    workbook,
    setWorkbook,
  ] = useState();

  // Search functionality
  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');
  const [
    filteredData,
    setFilteredData,
  ] = useState([]);
  const [
    itemsPerPage,
    setItemsPerPage,
  ] = useState(20);
  const [
    filterModalShow,
    setFilterModalShow,
  ] = useState(false);
  const [
    selectedDocument,
    setSelectedDocument,
  ] = useState({ filename: '' });
  const [
    clientList,
    setClientList,
  ] = useState([]);

  const [
    docTypeList,
    setDocTypeList,
  ] = useState([]);

  let indexOfLastItem = currentPage * itemsPerPage;
  let indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const [
    currentItems,
    setCurrentitems,
  ] = useState(filteredData.slice(indexOfFirstItem, indexOfLastItem));

  useEffect(() => {
    // When filters change, reset to page 1 and recalc visible items so the user always returns to the first page.
    setCurrentPage(1);
    let indexOfLastItem = 1 * itemsPerPage;
    let indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setCurrentitems(filteredData.slice(indexOfFirstItem, indexOfLastItem));
  }, [filteredData]);

  useEffect(() => {
    // Calculate the items for the current page
    let indexOfLastItem = currentPage * itemsPerPage;
    let indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setCurrentitems(filteredData.slice(indexOfFirstItem, indexOfLastItem));
  }, [currentPage]);

  // Popup
  const [
    show,
    setShow,
  ] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [
    backdropOpen,
    setBackdropOpen,
  ] = useState(false);
  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };
  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };

  const [
    reloadButtonDisabled,
    setReloadButtonDisabled,
  ] = useState(false);
  const [
    isLoading,
    setIsLoading,
  ] = useState(false);
  const [
    sessionExpired,
    setSessionExpired,
  ] = useState(false);

  function isOlderThanDays(dateString, days) {
    const inputDate = new Date(dateString);
    const now = new Date();

    // Difference in milliseconds
    const diffMs = now - inputDate;

    // Convert ms → days
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays > days; // true if older than 7 days
  }

  async function reload() {
    // if(!reloadButtonDisabled){
    //     setCurrentPage(1)
    // setSelectedGroup({clientId: -1, clientName: '', clientAbn: '', startDate: '', endDate: '', version: -1})

    setReloadButtonDisabled(true); // Disable the reload button
    try {
      let response = await fetch(`${window.origin}/api/omni2?count=50&start=0`, {
        headers: {
          'Authorization': `Bearer ${auth.user?.access_token}`,
          'Cache-Control': 'no-cache',
          'If-None-Match': '0',
        },
      });

      if (response.status === 403) {
        setSessionExpired(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      let data = await response.json();
      console.log(data);
      setRawData(data.data);
      setData(data.data);
      let clients = groupByClientIdAndDocumentType(data.data);
      let docTypes = groupByDocumentType(data.data);
      console.log(clients);
      setClientList(clients);
      setDocTypeList(docTypes);
      setFilteredData(data.data);
      console.log(data);
    } catch (e) {
      console.log(e);
    }
  }

  // Filters all documents by client fields + document fields whenever searchTerm changes.
  useEffect(() => {
    const lower = searchTerm.toLowerCase();

    setFilteredData(
      data.filter(row =>
        (row.rp_abn && row.rp_abn.includes(searchTerm))
        || (row.client_name && row.client_name.toLowerCase().includes(lower))
        || (row.document_type && row.document_type.toLowerCase().includes(lower))
        || (row.filename && row.filename.toLowerCase().includes(lower))
        || (row.document_name && row.document_name.toLowerCase().includes(lower)),
      ),
    );
  }, [
    searchTerm,
    data,
  ]);

  useEffect(() => {
    // Calculate the items for the current page
    let indexOfLastItem = currentPage * itemsPerPage;
    let indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setCurrentitems(transformedData.slice(indexOfFirstItem, indexOfLastItem));
  }, [currentPage]);

  const reloadWithLoading = async () => {
    setIsLoading(true); // Start showing the loading spinner
    handleBackdropOpen();
    await reload();
    setIsLoading(false); // Start showing the loading spinner
    handleBackdropClose();
  };

  useEffect(() => {
    if (displayMode == 'folder') {
      setItemsPerPage(12);
    } else {
      setItemsPerPage(8);
    }
    reloadWithLoading();
    reload();
  }, [displayMode]);

  function filterDataHandler(start_date, end_date, clients, doc_types, period) {
    if (start_date && start_date.reset) {
      setData(rawData);
      return;
    }
    console.log(start_date, end_date, clients, doc_types);

    let filteredRows = rawData.filter(doc => true);

    if (clients.length > 0) {
      filteredRows = filteredRows.filter(doc => clients.includes(doc.client_id));
    }

    if (doc_types.length > 0) {
      filteredRows = filteredRows.filter(doc => doc_types.includes(doc.document_type));
    }
    console.log(filteredRows);

    if (period == 'week') {
      filteredRows = filteredRows.filter(doc => !isOlderThanDays(doc.created_at, 7));
    } else if (period == 'month') {
      filteredRows = filteredRows.filter(doc => !isOlderThanDays(doc.created_at, 30));
    } else if (period == 'quarter') {
      filteredRows = filteredRows.filter(doc => !isOlderThanDays(doc.created_at, 120));
    } else if (period == 'year') {
      filteredRows = filteredRows.filter(doc => !isOlderThanDays(doc.created_at, 365));
    }

    setData(filteredRows);
  }

  return (
    <div className={props.imgClass} style={{ textAlign: 'center', position: 'relative', display: 'flex', flexDirection: 'row', width: '100%' }}>
      <SessionExpiredModal show={sessionExpired} />
      <div className="mainContainer" style={{ padding: 0, fontSize: '0.9vw', backgroundColor: 'rgb(254, 254, 255, 0.85)', border: '2px solid rgb(254, 254, 255, 0.3)', margin: '2vw', padding: '1vw', borderRadius: '2vw', width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'row', lineHeight: '1vw', alignItems: 'center', padding: '0.5vw 0.5vw', paddingBottom: '1.5vw' }}>

          <div style={{ textAlign: 'left', marginBottom: '0.5vw', display: 'flex', flexDirection: 'row', width: '100%' }}>
            <div style={{ backgroundColor: 'rgb(254, 254, 255, 0.5)', borderRadius: '0.8vw', padding: '0.5vw', paddingLeft: '1vw', width: '15vw', color: 'rgb(0, 15, 30, 0.7)' }}>
              <ThemeProvider theme={darkTheme}>

                <SearchIcon style={{ fontSize: '1vw' }} />
              </ThemeProvider>

                <Form.Control
                id="client-search-bar"
                style={{
                  display: 'inline', backgroundColor: 'rgba(255,255,255,0)', padding: 0, paddingLeft: '0.5vw',
                  border: '1px solid rgb(254, 254, 255, 0)', borderRadius: '6px', fontSize: '0.9vw', maxWidth: '50%', marginLeft: '0', fontWeight: '200',
                }}
                className="shadow-none"
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={{ flex: 1, textAlign: 'right', paddingLeft: '1vw', paddingRight: '0' }}>
              <OverlayTrigger
                key="top-submit"
                placement="top"
                overlay={(
                  <Tooltip id="tooltip-submit">
                    Filter
                  </Tooltip>
                )}
              >
                <TextButton id="create-new-workpaper" variant="text" style={{ color: 'rgb(0, 15, 30, 0.6)', fontSize: '0.9vw', cursor: 'pointer', padding: '0.8vw', minWidth: 0, borderRadius: '1vw', backgroundColor: 'rgb(254, 254, 255, 0.5)', marginLeft: '1vw' }} onClick={() => { setFilterModalShow(true); }}>
                  <FilterAltOutlinedIcon style={{ fontSize: '0.9vw', width: '0.95vw', height: '0.95vw' }} />
                </TextButton>

              </OverlayTrigger>
            </div>
          </div>
          </div>
          <DocumentList setPdfDisplayShow={setPdfDisplayShow} setSelectedDocument={setSelectedDocument} setSelectedGroup={setSelectedGroup} filteredData={filteredData} isLoading={isLoading} />
        </div>
        <FilterDocument show={filterModalShow} onHide={() => { setFilterModalShow(false); }} clientList={clientList} docTypeList={docTypeList} filterDataHandler={filterDataHandler} />
        <ExcelDisplay show={excelDisplayShow} workbook={workbook} onHide={() => { setExcelDisplayShow(false); }} />
        <PDFDisplay show={pdfDisplayShow} onHide={() => { setPdfDisplayShow(false); }} selectedClient={selectedGroup} selectedDocument={selectedDocument} onShow={setPdfDisplayShow} />
    </div>
  );
};
